// bridge so that the backend can determine all the routes that may be used
import urlJoin from 'url-join';
import { Entries } from 'type-fest';

export const clientRoutes = {
    root: '',
    login: {
        path: 'login',
        routes: {
            loginAdmin: 'admin',
            loginReadonly: 'readonly',
            loginAdminOrReadonly: 'admin-or-readonly',
            loginPlayer: 'player'
        }
    },
    admin: {
        path: 'admin',
        routes: {
            adminPanel: 'panel',
            storyBoard: 'storyboard',
            storyBoardSettings: 'storyboard/settings',
            storyBoardTargets: 'storyboard/targets',
            storyBoardDevices: 'storyboard/devices',
            storyBoardWebhooks: 'storyboard/webhooks',
            storyBoardDecorations: 'storyboard/decorations',
            teamSummaryCountries: 'team-summary/countries'
        }
    },
    analysis: {
        path: 'analysis',
        routes: {
            teamList: 'team-list',
            eventHistory: 'event-history'
        }
    },
    diagnostics: {
        path: 'diagnostics',
        routes: {
            troubleshooting: 'troubleshooting',
            teamDebug: 'team-debug',
            diagnosticsFlags: 'flags',
            maintenance: 'maintenance',
            logs: 'logs',
            clientTrackingDebug: 'client-tracking'
        }
    },
    security: {
        path: 'security',
        routes: {
            ipRestrictions: 'ip-restrictions'
        }
    },
    commentator: {
        path: 'commentator',
        routes: {
            commentatorInfo: 'info',
            commentatorObjectives: 'objectives',
            playerAssetUpload: 'player-assets'
        }
    },
    info: {
        path: 'info',
        routes: {
            docs: 'docs',
            changelog: 'changelog'
        }
    },
    supportRequest: {
        path: 'support-request',
        routes: {
            supportRequestCreate: 'create',
            supportRequestList: 'list',
            adminSupportRequestCreate: 'admin-create',
            adminSupportRequestList: 'admin-list'
        }
    }
};

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void
    ? I
    : never;

type CR = typeof clientRoutes;
// gather types of all clientroute values
type CV = { [K in keyof CR]: CR[K] extends { routes: unknown } ? CR[K] : never }[keyof CR];
// get the Union of all types of all routes values
type RoutesRecord = CV['routes'];
// convert Union '|' to intersection '&'
type FullClientRoutes = UnionToIntersection<{ [K in keyof CR]: string } | RoutesRecord>;

// this is every key from the top level plus every key of the fullClientRoutes[k].routes
// with url paths concatenated as appropriate
export const fullClientRoutes = (Object.entries(clientRoutes) as Entries<CR>).reduce(
    (r, [k, v]) => {
        if (typeof v === 'string') {
            r[k] = v;
        } else {
            r[k] = v.path;
            if ('routes' in v) {
                for (const [routeName, routePath] of Object.entries(v.routes) as Entries<
                    (typeof v)['routes']
                >) {
                    r[routeName] = urlJoin(v.path, routePath);
                }
            }
        }
        return r;
    },
    {} as FullClientRoutes
);
